import { useCallback, useEffect, useRef, useState } from 'react';
import { UnauthorizedError, api, initApi } from './api';
import { currentEmail, getIdToken, initAuth, signOut } from './auth';
import { Calendar } from './components/Calendar';
import { ChartView } from './components/ChartView';
import { DayEditor } from './components/DayEditor';
import { ExportSheet } from './components/ExportSheet';
import { Icon } from './components/Icon';
import { Login } from './components/Login';
import { Praise } from './components/Praise';
import { preloadPersonaImages } from './components/Avatars';
import type { Praise as PraiseData } from './praise';
import { loadConfig } from './config';
import { type Month, monthRange, thisMonth, today } from './dates';
import { MOCK, resetMockData } from './mock';
import type { Workout } from './types';

type Boot = { state: 'loading' } | { state: 'ready'; signedIn: boolean } | { state: 'error'; message: string };
type Toast = { message: string; kind: 'ok' | 'error' };

export function App() {
  const [boot, setBoot] = useState<Boot>({ state: 'loading' });
  const [month, setMonth] = useState<Month>(thisMonth);
  const [workouts, setWorkouts] = useState<Map<string, Workout>>(new Map());
  const [selected, setSelected] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [praise, setPraise] = useState<PraiseData | null>(null);
  const [view, setView] = useState<'calendar' | 'chart'>('calendar');

  const toastTimer = useRef<number | undefined>(undefined);

  const showToast = useCallback((message: string, kind: 'ok' | 'error' = 'ok') => {
    setToast({ message, kind });
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 3000);
  }, []);

  // --- 起動: 接続設定を読み、保存済みセッションがあればそのまま入る
  useEffect(() => {
    (async () => {
      try {
        const config = await loadConfig();
        initAuth(config);
        initApi(config.apiUrl);
        const signedIn = (await getIdToken()) !== null;
        // 保存直後に出るので、似顔絵は先に読み込んでおく
        if (signedIn) preloadPersonaImages();
        setBoot({ state: 'ready', signedIn });
      } catch (err) {
        setBoot({ state: 'error', message: err instanceof Error ? err.message : '起動に失敗しました' });
      }
    })();
  }, []);

  const signedIn = boot.state === 'ready' && boot.signedIn;

  const handleAuthLoss = useCallback(() => {
    signOut();
    setWorkouts(new Map());
    setSelected(null);
    setExporting(false);
    setBoot({ state: 'ready', signedIn: false });
  }, []);

  const reportError = useCallback(
    (err: unknown) => {
      if (err instanceof UnauthorizedError) {
        handleAuthLoss();
        return;
      }
      showToast(err instanceof Error ? err.message : '処理に失敗しました', 'error');
    },
    [handleAuthLoss, showToast],
  );

  // --- 表示中の月の記録を読む
  useEffect(() => {
    if (!signedIn) return;
    let cancelled = false;
    const { from, to } = monthRange(month);

    api
      .listWorkouts(from, to)
      .then((list) => {
        if (cancelled) return;
        // 既読の月と混ぜて保持する (種目候補の充実と、月をまたぐ再描画の抑制)
        setWorkouts((prev) => {
          const next = new Map(prev);
          for (const key of next.keys()) {
            if (key >= from && key <= to) next.delete(key);
          }
          for (const w of list) next.set(w.date, w);
          return next;
        });
      })
      .catch((err: unknown) => {
        if (!cancelled) reportError(err);
      });

    return () => {
      cancelled = true;
    };
  }, [month, signedIn, reportError]);

  const applySaved = useCallback(
    (date: string, workout: Workout | null, nextPraise: PraiseData | null) => {
      setWorkouts((prev) => {
        const next = new Map(prev);
        // 体組成だけの日も記録として保持する (グラフで使う)
        if (workout) next.set(date, workout);
        else next.delete(date);
        return next;
      });
      // 強度のしきい値は直近の記録から決まるため、判定は DayEditor 側で済ませてある。
      // 削除のときは null が来る (褒めない)。
      setPraise(nextPraise);
    },
    [],
  );

  if (boot.state === 'loading') {
    return (
      <div className="loading">
        <span className="spinner" />
      </div>
    );
  }

  if (boot.state === 'error') {
    return (
      <div className="loading">
        <div className="alert" style={{ maxWidth: 420 }}>
          {boot.message}
        </div>
      </div>
    );
  }

  if (!boot.signedIn) {
    return <Login onSignedIn={() => setBoot({ state: 'ready', signedIn: true })} />;
  }

  return (
    <>
      <header className="topbar">
        <div className="brand">
          <div className="brand__mark" aria-hidden>
            鍛
          </div>
          <div className="brand__name">FORGE</div>
        </div>
        <div className="topbar__spacer" />
        {MOCK ? (
          <>
            <span className="badge">MOCK</span>
            <button
              className="iconbtn"
              onClick={() => {
                resetMockData();
                location.reload();
              }}
              aria-label="モックデータをリセット"
              title="モックデータをリセット"
            >
              <Icon name="refresh" />
            </button>
          </>
        ) : (
          <>
            <span className="label" style={{ maxWidth: 150, overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {currentEmail()}
            </span>
            <button className="iconbtn" onClick={handleAuthLoss} aria-label="ログアウト" title="ログアウト">
              <Icon name="power" />
            </button>
          </>
        )}
      </header>

      <nav className="viewtabs" role="tablist" aria-label="表示の切り替え">
        {(
          [
            ['calendar', 'カレンダー'],
            ['chart', 'グラフ'],
          ] as const
        ).map(([id, label]) => (
          <button
            key={id}
            className="viewtab"
            role="tab"
            aria-selected={view === id}
            onClick={() => setView(id)}
          >
            {label}
          </button>
        ))}
      </nav>

      <main className="shell">
        {view === 'calendar' ? (
          <Calendar
            month={month}
            onMonthChange={setMonth}
            workouts={workouts}
            selected={selected}
            onSelect={setSelected}
          />
        ) : (
          <ChartView onError={reportError} />
        )}
      </main>

      <div className="dock">
        <div className="dock__inner">
          <button className="btn" onClick={() => setExporting(true)}>
            Claude へ送る
          </button>
          <button className="btn btn--primary" onClick={() => setSelected(today())}>
            今日を記録
          </button>
        </div>
      </div>

      {selected && (
        <DayEditor
          key={selected}
          date={selected}
          onClose={() => setSelected(null)}
          onSaved={applySaved}
          onError={reportError}
        />
      )}

      {exporting && (
        <ExportSheet onClose={() => setExporting(false)} onToast={showToast} onError={reportError} />
      )}

      {praise && <Praise praise={praise} onDismiss={() => setPraise(null)} />}

      {toast && <div className={`toast${toast.kind === 'error' ? ' toast--error' : ''}`}>{toast.message}</div>}
    </>
  );
}
